import { useSearchParams, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";

import { getAllActivitiesOfAdminByDateQueryOptions } from "@/config/queries/superadmin-query-options";
import { useMYPicker } from "./_components/month-year-picker/use-my-picker";
import moment from "moment";
import { convertUTCtoIST } from "@/utils";
import { MonthYearPicker } from "./_components/month-year-picker/month-year-picker";

// Register the zoom plugin
ChartJS.register(zoomPlugin);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MonthDates {
  startDate: Date;
  endDate: Date;
}

function secondsToTimeString(seconds: number | null): string {
  if (seconds === null) {
    return "Not Found";
  }
  // Calculate hours, minutes, and determine AM/PM
  const hours = Math.floor(seconds / 3600) % 24;
  const minutes = Math.floor((seconds % 3600) / 60);
  const period = hours >= 12 ? "PM" : "AM";

  // Adjust hours for PM notation
  const adjustedHours = hours % 12 === 0 ? 12 : hours % 12;

  // Format the hours and minutes as HH:mm with AM/PM notation
  const formattedHours = String(adjustedHours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");

  // Return the formatted time string
  return `${formattedHours}:${formattedMinutes}${period}`;
}

function getTotalSecondsFromDate(d: string): number {
  const date = new Date(d);

  // Get hours, minutes, and seconds from the given date
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Calculate total seconds
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  return totalSeconds;
}

function getMonthDates(month: number, year: number): MonthDates {
  // Moment.js starts months from 0, so we subtract 1 from the input month
  const startDate = moment([year, month]).startOf("month").toDate();
  const endDate = moment([year, month]).endOf("month").toDate();

  return {
    startDate,
    endDate,
  };
}

function generateMonthDates(
  month: number,
  year: number
): { [date: string]: string } {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  const monthDates: { [date: string]: string } = {};

  for (
    let date = startDate;
    date <= endDate;
    date.setDate(date.getDate() + 1)
  ) {
    const dateString = format(date, "PP");
    const key = date.getDate();
    monthDates[key] = dateString;
  }

  return monthDates;
}

const IndividualActivityPageOfAdminForSA = () => {
  const key = Cookies.get("key") as string;

  const { my } = useMYPicker();

  const [searchParams] = useSearchParams();
  const { adminId } = useParams() as {
    adminId: string;
  };

  const name = searchParams.get("n");

  const { startDate, endDate } = getMonthDates(my.month, my.year);

  const start = startDate;
  const end = endDate;

  const { data, isPending, isFetching } = useQuery(
    getAllActivitiesOfAdminByDateQueryOptions(
      key,
      convertUTCtoIST(new Date(start.setHours(0))),
      convertUTCtoIST(new Date(end.setHours(23))),
      adminId
    )
  );

  const adminActivities = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.activity === undefined) {
      return [];
    }

    const logins = data?.activity?.filter((entry) => entry?.mode === "LOGIN");

    const logouts = data?.activity?.filter((entry) => entry?.mode === "LOGOUT");

    const pairedLogs: {
      inLog: {
        mode: string;
        createdAt: string;
      };
      outLog: {
        mode: string;
        createdAt: string;
      } | null;
    }[] = [];

    for (const inLog of logins) {
      const correspondingOutLog = logouts?.find(
        (outlog) =>
          new Date(outlog?.createdAt).getDate() ===
            new Date(inLog?.createdAt).getDate() &&
          !pairedLogs.some((pair) => pair.outLog === outlog)
      );

      if (correspondingOutLog) {
        pairedLogs.push({ inLog, outLog: correspondingOutLog });
      } else {
        pairedLogs.push({
          inLog,
          outLog: null,
        });
      }
    }

    let result: {
      loginTime: number;
      logoutTime: number | undefined;
      date: string;
      y: number;
    }[] = [];

    for (const pair of pairedLogs) {
      if (pair.outLog !== null) {
        const loginTime = getTotalSecondsFromDate(pair.inLog?.createdAt);
        const logoutTime = getTotalSecondsFromDate(pair.outLog?.createdAt);
        const date = format(new Date(pair.inLog?.createdAt), "PP");
        const y = new Date(pair.inLog?.createdAt)?.getDate();

        result.push({
          loginTime,
          logoutTime,
          date,
          y,
        });
      } else {
        const loginTime = getTotalSecondsFromDate(pair.inLog?.createdAt);
        const logoutTime = undefined;
        const date = format(new Date(pair.inLog?.createdAt), "PP");
        const y = new Date(pair.inLog?.createdAt)?.getDate();

        result.push({
          loginTime,
          logoutTime,
          date,
          y,
        });
      }
    }

    return result;
  }, [data, isPending, isFetching]);

  const labels2 = [
    // 8:00am
    28800,

    // 9:00am
    32400,

    // 10:00am
    36000,

    // 11:00am
    39600,

    // 12:00pm
    43200,

    // 1:00pm
    46800,

    // 2:00pm
    50400,

    // 3:00pm
    54000,

    // 4:00pm
    57600,

    // 5:00pm
    61200,

    // 6:00pm
    64800,

    // 7:00pm
    68400,

    // 8:00pm
    72000,
  ];

  const completeChartData = useMemo(() => {
    let refinedLabels = labels2;

    adminActivities?.forEach((act) => {
      if (!refinedLabels?.includes(act?.loginTime)) {
        refinedLabels?.push(act?.loginTime);
      }

      if (act?.logoutTime && !refinedLabels?.includes(act?.logoutTime)) {
        refinedLabels?.push(act?.logoutTime);
      }
    });

    refinedLabels = refinedLabels.sort((a, b) => a - b);

    const allDates = generateMonthDates(my.month, my.year);

    let datasets: {
      id: string;
      label: string;
      data: {
        x: number | undefined;
        y: number | undefined;
      }[];
    }[] = Object.keys(allDates).map((day) => {
      return {
        id: day,
        label: allDates[Number(day)],
        data: [],
      };
    });

    adminActivities?.forEach((act) => {
      const isAlreadyExist = datasets?.find((d) => d?.id === String(act?.y));

      if (!isAlreadyExist) {
        datasets.push({
          id: String(act.y),
          label: act.date,
          data: [
            {
              x: act.loginTime,
              y: act.y,
            },
            {
              x: act.logoutTime,
              y: act.y,
            },
          ],
        });
      } else {
        datasets = datasets.map((dataset) => {
          if (dataset.id === isAlreadyExist.id) {
            return {
              ...isAlreadyExist,
              data: [
                ...isAlreadyExist.data,
                {
                  x: undefined,
                  y: undefined,
                },
                {
                  x: act.loginTime,
                  y: act.y,
                },
                {
                  x: act.logoutTime,
                  y: act.y,
                },
              ],
            };
          }

          return dataset;
        });
      }
    });

    return {
      labels: refinedLabels,
      datasets,
    };
  }, [adminActivities, labels2, my]);

  const yMapping: {
    [key: string]: string;
  } = useMemo(() => {
    const result: {
      [key: string]: string;
    } = {};

    const allDates = generateMonthDates(my.month, my.year);

    for (const day of Object.keys(allDates)) {
      result[day] = allDates[day];
    }

    return result;
  }, [adminActivities, my]);

  return (
    <div className="p-2 h-screen">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">
          {name ? `${name}'s Activity` : `Admin's Activity`}
        </h3>

        <MonthYearPicker />
      </div>

      <div className="h-[400px] mt-10 mx-10">
        <Line
          data={completeChartData}
          options={{
            scales: {
              y: {
                min: 0,
                ticks: {
                  callback: (value) => {
                    const find = yMapping[String(value)];
                    return find;
                  },
                  stepSize: 1,
                  autoSkip: false,
                },
              },
              x: {
                ticks: {
                  callback: (value) => {
                    const find = completeChartData.labels[value as number];

                    return secondsToTimeString(find);
                  },
                },
              },
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              zoom: {
                pan: {
                  enabled: true,
                  mode: "x",
                },
                zoom: {
                  wheel: {
                    enabled: true,
                  },
                  pinch: {
                    enabled: true,
                  },
                  mode: "x",
                },
              },
              tooltip: {
                callbacks: {
                  title: (tooltipItems) => {
                    const dataindex = tooltipItems[0].dataIndex;
                    const datasetIndex = tooltipItems[0].datasetIndex;
                    const dataset = completeChartData.datasets[datasetIndex];
                    const find = dataset.data?.find((_, i) => i === dataindex);

                    return secondsToTimeString(find!.x as number);
                  },
                },
              },
              legend: {
                display: false,
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default IndividualActivityPageOfAdminForSA;
